"use client";

import { FetchError, ofetch } from "ofetch";
import useSWRMutation from "swr/mutation";
import * as z from "zod";

import {
  ForgotPasswordApiErrorResponse,
  ForgotPasswordApiSuccessResponse,
} from "@/api/auth/forgot-password/route";
import Button from "@/components/atoms/Button";
import TestingModeOnly from "@/components/blocks/TestingModeOnly";
import TextInput from "@/components/forms/controls/TextInput";
import Form from "@/components/forms/Form";
import { useZodForm } from "@/hooks/useZodForm";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

interface ForgotPasswordProps {
  locale: AllowedLanguages;
}

export const ForgotPasswordForm = ({ locale }: ForgotPasswordProps) => {
  const { t } = useTranslation(locale);

  const schema = z.object({
    email: z.email(t("common.error.invalid_email")).trim().toLowerCase(),
  });

  const form = useZodForm({ schema });

  async function forgotPassword(
    url: string,
    { arg }: { arg: z.infer<typeof schema> },
  ) {
    return await ofetch<ForgotPasswordApiSuccessResponse>(url, {
      method: "POST",
      body: {
        locale: locale,
        ...arg,
      },
    });
  }

  const { data, trigger, isMutating, error } = useSWRMutation<
    ForgotPasswordApiSuccessResponse,
    FetchError<ForgotPasswordApiErrorResponse> | Error,
    string,
    z.infer<typeof schema>
  >("/api/auth/forgot-password", forgotPassword);

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    await trigger(data);
  };

  if ((error as { status?: number } | undefined)?.status === 409) {
    return (
      <div>
        <h5>
          {t("pages.myvivaldis_forgot_password.forgot_password_form.subtitle")}
        </h5>
        <div className="form-error mt-4 text-lg">
          {t(
            "pages.myvivaldis_forgot_password.forgot_password_form.error.user_not_active",
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {data?.ok === true ? (
        <div>
          <h5>
            {t(
              "pages.myvivaldis_forgot_password.forgot_password_form.success.title",
            )}
          </h5>
          <div className="text-lg text-black-400">
            {t(
              "pages.myvivaldis_forgot_password.forgot_password_form.success.message",
            )}
          </div>
          <TestingModeOnly>
            <div className="mt-4 rounded border-2 border-dashed bg-yellow-100 p-4">
              <h6>Reset url (testing)</h6>
              <a href={data.url ?? "#"} className="break-all text-sm">
                {data.url}
              </a>
            </div>
          </TestingModeOnly>
        </div>
      ) : (
        <>
          <h5>
            {t(
              "pages.myvivaldis_forgot_password.forgot_password_form.subtitle",
            )}
          </h5>
          <div className="mb-6 text-sm italic text-black-400">
            {t(
              "pages.myvivaldis_forgot_password.forgot_password_form.description",
            )}
          </div>
          <Form
            form={form}
            className="flex flex-col gap-4"
            onSubmit={handleSubmit}
            noValidate={true}
          >
            <TextInput
              label={t("common.form.email.label")}
              placeholder={t("common.form.email.placeholder")}
              name="email"
              type="email"
              autoComplete="email"
              required
            />
            {error && (
              <div className="text-red">
                {t(
                  "pages.myvivaldis_forgot_password.forgot_password_form.error.unknown_error",
                )}
              </div>
            )}

            <Button
              type="submit"
              className="btn-lg ml-auto mt-6"
              disabled={isMutating}
              hasLoader={true}
              isLoading={isMutating}
            >
              {t(
                "pages.myvivaldis_forgot_password.forgot_password_form.submit",
              )}
            </Button>
          </Form>
        </>
      )}
    </>
  );
};
